import React, { MemoExoticComponent, ReactNode } from 'react';
import classNames from 'classnames';
import spriteUrl from '@uswds/uswds/dist/img/sprite.svg';
import { MaterialIcons, getMaterialIconSrc } from './allMaterialIcons';
import { USWDSIcons } from './USWDSIcons';
import allUSWDSIcons from './allUSWDSIcons';

interface IconWithName {
  name: USWDSIcons | MaterialIcons;
  src?: never;
}

interface IconWithSrc {
  name?: never;
  src: string;
}

interface IconCommonProps {
  className?: string;
  title?: {
    id: string;
    text: ReactNode;
  };
  size?: undefined | 3 | 4 | 5 | 6 | 7 | 8 | 9;
}

export type IconProps = (IconWithName | IconWithSrc) & IconCommonProps;

let onlyIdentifier = false;
/**
 * Use this utility if you need to load icons from a different domain
 * than you are hosting your app.
 *
 * https://css-tricks.com/ajaxing-svg-sprite/
 */
function setOnlyIdentifier() {
  onlyIdentifier = true;
}

function getMaterialIconByName(name: string | undefined) {
  const materialIconSrc = getMaterialIconSrc(name as MaterialIcons);
  return materialIconSrc ? <image href={materialIconSrc} height="100%" width="100%" /> : null;
}

function IconComponent({ name, src, className, size, title }: IconProps): JSX.Element {
  return (
    <svg
      className={classNames('usa-icon', className, size && `usa-icon--size-${size}`)}
      aria-hidden={title === undefined}
      focusable="false"
      role="img"
      aria-labelledby={title?.id}
    >
      {title !== undefined ? <title id={title.id}>{title.text}</title> : null}
      {allUSWDSIcons.includes(name as USWDSIcons) ? (
        <use xlinkHref={`${onlyIdentifier ? '' : spriteUrl}#${name as string}`} />
      ) : (
        getMaterialIconByName(name)
      )}
      {src ? <image href={src} height="100%" width="100%" /> : null}
    </svg>
  );
}

const Icon = React.memo(IconComponent) as MemoExoticComponent<typeof IconComponent> & {
  spriteUrl: string;
  setOnlyIdentifier: VoidFunction;
};

Icon.spriteUrl = spriteUrl;
Icon.setOnlyIdentifier = setOnlyIdentifier;

export default Icon;
